import axios, { CanceledError } from 'axios';
import { config } from '../config/env';
import { handle403, redirectToLogin } from './auth-handler';
import { refreshAccessToken } from './tokenRefresh';

const API_BASE_URL = config.API_BASE_URL;

const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor for debugging
API.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to:`, `${config.baseURL}${config.url}`);
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
API.interceptors.response.use(
  (response) => {
    console.log(`Response received:`, response.status, response.statusText);
    return response.data;
  },
  async (error) => {
    // Story 6.3 follow-up: cancellations are expected (component unmount,
    // React StrictMode double-invoke, AbortController.abort() during a new
    // poll cycle). Don't pollute the console — just propagate the rejection
    // for callers that care to filter on it.
    if (error instanceof CanceledError || error?.code === 'ERR_CANCELED' || axios.isCancel?.(error)) {
      return Promise.reject({
        status: -1,
        message: 'canceled',
        code: 'ERR_CANCELED',
        type: 'canceled',
      });
    }

    console.error('API Error:', error);

    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      console.error(`API Error ${status}:`, data);

      // Handle 403 Forbidden — permission denied (delegated to auth-handler)
      if (status === 403) {
        const detail =
          typeof data.detail === 'string' ? data.detail : data.detail?.message || '';
        const { isPermission, message } = handle403(detail, data);
        return Promise.reject({
          status: 403,
          permissionDenied: isPermission,
          message,
          ...data,
        });
      }

      // Handle 401 Unauthorized
      if (status === 401) {
        console.warn('🔒 401 Unauthorized - Session expired or invalid');
        // JWT_EXPIRED = the access JWT lapsed but the session is still valid.
        // Silently re-mint and retry once; only a dead session redirects to login.
        const original = error.config;
        const code = error.response.headers?.['x-error-code'];
        if (code === 'JWT_EXPIRED' && original && !original._retry) {
          original._retry = true;
          if (await refreshAccessToken()) {
            return API(original);
          }
        }
        redirectToLogin();
        return Promise.reject(new Error('Authentication required - redirecting to login'));
      }

      // Return structured error for other status codes
      return Promise.reject({
        status,
        message: data.message || data.detail || 'An error occurred',
        ...data
      });
    } else if (error.request) {
      // Network error
      console.error('Network Error:', error.message);
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your connection and try again.',
        type: 'network'
      });
    } else {
      // Other error
      console.error('Request Error:', error.message);
      return Promise.reject({
        status: -1,
        message: error.message || 'An unexpected error occurred',
        type: 'unknown'
      });
    }
  }
);

/**
 * Probe the backend health endpoint. Mirrors AthenaBackendService.healthCheck().
 *
 * Returns a boolean (NOT the parsed body) — preserves the legacy method's
 * `response.ok` semantics. Any thrown error from the interceptor is caught
 * and reported as `false` so callers can use this as a simple liveness probe.
 *
 * `/health` lives at the FastAPI root (no `/api/v1` prefix), hence the
 * `{ baseURL: '' }` override.
 */
export async function healthCheck() {
  try {
    await API.get('/health', { baseURL: '' });
    return true;
  } catch {
    return false;
  }
}

export default API;
